// TextField — labelled input with optional helper/error and icons.

import { useId, type InputHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  error?: string;
  helperText?: string;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  size?: "sm" | "md";
}

export function TextField({
  label,
  error,
  helperText,
  leadingIcon,
  trailingIcon,
  size = "md",
  className,
  id,
  ...rest
}: TextFieldProps) {
  const auto = useId();
  const inputId = id ?? auto;
  const hint = error ?? helperText;
  return (
    <label className={cn("lex-tf", error && "lex-tf--error", className)} data-size={size} htmlFor={inputId}>
      {label ? <span className="lex-tf__label">{label}</span> : null}
      <span className="lex-tf__box">
        {leadingIcon ? <span className="lex-tf__slot" aria-hidden="true">{leadingIcon}</span> : null}
        <input
          id={inputId}
          className="lex-tf__input"
          aria-invalid={error ? true : undefined}
          aria-describedby={hint ? `${inputId}-hint` : undefined}
          {...rest}
        />
        {trailingIcon ? <span className="lex-tf__slot" aria-hidden="true">{trailingIcon}</span> : null}
      </span>
      {hint ? <span id={`${inputId}-hint`} className="lex-tf__hint">{hint}</span> : null}
    </label>
  );
}

export default TextField;
